
// composition-api
import { computed, defineComponent, nextTick, reactive, ref, watch } from "vue";
// helpers

import routeGuard from "@/helpers/routeGuard";
//tipos
import { FiltrosPesaje } from "@/typings/components/pesajes/filtros";
import {
  Pesaje,
  PesajePatch,
} from "@/typings/store/plugins/easyFirestore/pesajes";
import { Chofer } from "@/typings/store/plugins/easyFirestore/choferes";
import { Desecho } from "@/typings/store/plugins/easyFirestore/desechos";
import { Cliente } from "@/typings/store/plugins/easyFirestore/clientes";
import { Servicio } from "@/typings/store/plugins/easyFirestore/servicios";
import { Vehiculo } from "@/typings/store/plugins/easyFirestore/vehiculos";
import { RutaPesaje } from "@/typings/store/plugins/easyFirestore/rutasPesaje";
import {
  Archivo,
  BlobArchivo,
} from "@/typings/store/plugins/easyFirestore/archivos";

// composables
import { useI18n } from "@/composables/i18n";
import { useUsuario } from "@/composables/usuario";
import { usePesajes } from "@/composables/pesajes";
import { useUsuarios } from "@/composables/usuarios";
import { useArchivos } from "@/composables/archivos";
import { useDesechos } from "@/composables/desechos";
import { useClientes } from "@/composables/clientes";
import { useChoferes } from "@/composables/choferes";
import { useVehiculos } from "@/composables/vehiculos";
import { useServicios } from "@/composables/servicios";
import { useRutasPesaje } from "@/composables/rutasPesaje";
//componentes
import DetallePesaje from "@/components/pesajes/Detalle.vue";
import FiltrosPesajes from "@/components/pesajes/Filtros.vue";
import TablaPesajes from "@/components/pesajes/TablaPesajes.vue";
import SuccessDialog from "@/components/custom/SuccessDialog.vue";
import Formulario from "@/components/pesajes/FormularioPesaje.vue";
import GaleriaImagenes from "@/components/pesajes/GaleriaImagenes.vue";
import ConfirmationDialog from "@/components/custom/ConfirmationDialog.vue";

interface Imagenes {
  nuevas: Array<BlobArchivo>;
  eliminar: Array<Archivo>;
}

interface DialogoConfirmacion {
  model: boolean;
  texto: string;
  si: (() => void) | undefined;
  no: (() => void) | undefined;
}

export default defineComponent({
  name: "VistaPesajes",
  components: {
    Formulario,
    TablaPesajes,
    SuccessDialog,
    DetallePesaje,
    FiltrosPesajes,
    GaleriaImagenes,
    ConfirmationDialog,
  },
  beforeRouteEnter(_to, _from, next) {
    next(routeGuard);
  },
  created(): void {
    if (!routeGuard()) {
      return;
    }
    this.$eventBus.$on("click:editar-pesaje", this.onClickEditarPesaje);
    const firstDayOfMonth = new Date();
    firstDayOfMonth.setDate(1);
    this.filtros.fechas = [
      firstDayOfMonth.format("YYYY-MM-DD"),
      new Date().format("YYYY-MM-DD"),
    ];
  },
  destroyed(): void {
    this.$eventBus.$off("click:editar-pesaje", this.onClickEditarPesaje);
  },
  setup() {
    const { t } = useI18n();

    const _usuario = useUsuario();
    const _pesajes = usePesajes();
    const _usuarios = useUsuarios();
    const _archivos = useArchivos();
    const _desechos = useDesechos();
    const _clientes = useClientes();
    const _choferes = useChoferes();
    const _vehiculos = useVehiculos();
    const _servicios = useServicios();
    const _rutasPesaje = useRutasPesaje();

    const imagenes = reactive<Imagenes>({
      nuevas: [],
      eliminar: [],
    });

    const mostrarGaleria = ref(false);
    const editarGaleria = ref(false);
    const pesaje = ref<Pesaje | null>(null);
    const dialogoInfo = ref(false);
    const dialogoEditar = ref(false);
    const cargando = ref(false);

    const dialogoExito = reactive({
      model: false,
      mensaje: "",
      icono: "",
    });

    const dialogoConfirmacion: DialogoConfirmacion = reactive({
      model: false,
      texto: "",
      si: undefined,
      no: undefined,
    });

    const filtros = reactive<FiltrosPesaje>({
      search: "",
      clientes: [],
      vehiculo: null,
      servicios: [],
      errores: false,
      fechas: [],
    });

    const usuario = _usuario.get;

    const clientesUsuario = computed((): Array<Cliente> => {
      if (usuario.value?.clientes && usuario.value.clientes.length > 0) {
        return usuario.value.clientes;
      }
      return usuario.value?.cliente ? [usuario.value.cliente] : [];
    });

    if (clientesUsuario.value.length > 0) {
      filtros.clientes.push(...clientesUsuario.value);
    }

    const serviciosUsuario = computed((): Array<Servicio> => {
      return usuario.value?.servicios ?? [];
    });

    const usuarios = _usuarios.get;

    const clientes = _clientes.get;

    const servicios = _servicios.get;

    const permisos = _usuario.getPermisos;

    const archivos = _archivos.getArchivos;

    const vehiculosOrdenados = computed((): Array<Vehiculo> => {
      _vehiculos.setOrdenarArray(true);
      return _vehiculos.getArrayHabilitados.value;
    });

    const desechosOrdenados = computed((): Array<Desecho> => {
      _desechos.setOrdenarArray(true);
      return _desechos.getArray.value;
    });

    const clientesOrdenados = computed((): Array<Cliente> => {
      if (clientesUsuario.value.length > 0) return clientesUsuario.value;
      _clientes.setOrdenarArray(true);
      return _clientes.getArrayHabilitados.value;
    });

    const serviciosOrdenados = computed((): Array<Servicio> => {
      if (serviciosUsuario.value.length > 0) return serviciosUsuario.value;
      _servicios.setOrdenarArray(true);
      return _servicios.getArray.value;
    });

    const choferesOrdenados = computed((): Array<Chofer> => {
      _choferes.setOrdenarArray(true);
      return _choferes.getArrayHabilitados.value;
    });

    const rutasPesajeOrdenadas = computed((): Array<RutaPesaje> => {
      _rutasPesaje.setOrdenarArray(true);
      return _rutasPesaje.getArrayHabilitados.value;
    });

    const ordenarPesajes = (pesajes: Pesaje[]) => {
      pesajes.sort((p1, p2) => {
        const fecha1: Date | null = p1.entrada.fecha
          ? p1.entrada.fecha.toDate()
          : null;
        const fecha2: Date | null = p2.entrada.fecha
          ? p2.entrada.fecha.toDate()
          : null;
        if (!fecha1) return -1;
        if (!fecha2) return 1;
        return fecha2.getTime() - fecha1.getTime();
      });
      return pesajes;
    };

    const filtroFecha = (_pesaje: Pesaje) => {
      const fechaPesaje = _pesaje.entrada.fecha.toDate();
      const [fechaInicioString, fechaFinString] = filtros.fechas;
      const fechaInicio = fechaInicioString
        ? `${fechaInicioString} 00:00:00`.toDate()
        : new Date();
      const fechaFin = fechaFinString
        ? `${fechaFinString} 23:59:59`.toDate()
        : new Date();
      return fechaPesaje >= fechaInicio && fechaPesaje <= fechaFin;
    };

    const filtroVehiculo = (_pesaje: Pesaje) => {
      if (!filtros.vehiculo) return true;
      return filtros.vehiculo.patente === _pesaje.vehiculo?.patente;
    };

    const validarCliente = (_pesaje: Pesaje, cliente: Cliente): boolean => {
      if (!cliente.id) {
        return (
          !!_pesaje.cliente?.nombre &&
          !!cliente.nombre &&
          _pesaje.cliente.nombre.toLowerCase() === cliente.nombre.toLowerCase()
        );
      }
      if (!_pesaje.cliente) return false;
      if (!_pesaje.cliente.id) {
        return (
          !!_pesaje.cliente.nombre &&
          !!cliente.nombre &&
          _pesaje.cliente.nombre.toLowerCase() === cliente.nombre.toLowerCase()
        );
      }
      return (
        _pesaje.cliente.id === cliente.id &&
        _pesaje.cliente.nombre?.toLowerCase() === cliente.nombre?.toLowerCase()
      );
    };

    const validarServicio = (_pesaje: Pesaje, servicio: Servicio): boolean => {
      if (!servicio.id) {
        return (
          !!_pesaje.servicio?.nombre &&
          !!servicio.nombre &&
          _pesaje.servicio.nombre.toLowerCase() ===
            servicio.nombre.toLowerCase()
        );
      }
      if (!_pesaje.servicio) return false;
      if (!_pesaje.servicio.id) {
        return (
          !!_pesaje.servicio.nombre &&
          !!servicio.nombre &&
          _pesaje.servicio.nombre.toLowerCase() ===
            servicio.nombre.toLowerCase()
        );
      }
      return (
        _pesaje.servicio.id === servicio.id &&
        _pesaje.servicio.nombre?.toLowerCase() ===
          servicio.nombre?.toLowerCase()
      );
    };

    const filtroError = (_pesaje: Pesaje) => {
      if (!filtros.errores) return true;
      return (
        errorCliente(_pesaje) ||
        errorServicio(_pesaje) ||
        errorCapacidad(_pesaje) ||
        errorCampos(_pesaje) ||
        errorImg(_pesaje)
      );
    };

    const datosFiltrados = computed((): Array<Pesaje> => {
      let pesajes = _pesajes.getArray.value.filter((_pesaje) => {
        const _filtroFecha = filtroFecha(_pesaje);
        const _filtroVehiculo = filtroVehiculo(_pesaje);

        const filtroClientes = filtros.clientes;
        const _filtroCliente =
          clientesUsuario.value.length > 0
            ? clientesUsuario.value.some((cliente) =>
                validarCliente(_pesaje, cliente),
              )
            : filtroClientes.length > 0
            ? filtroClientes.some((cliente) => validarCliente(_pesaje, cliente))
            : true;

        const filtroServicios = filtros.servicios;
        const _filtroServicio =
          serviciosUsuario.value.length > 0
            ? serviciosUsuario.value.some((servicio) =>
                validarServicio(_pesaje, servicio),
              )
            : filtroServicios.length > 0
            ? filtroServicios.some((servicio) =>
                validarServicio(_pesaje, servicio),
              )
            : true;

        const _filtroError = filtroError(_pesaje);
        const _filtroEstado = _pesaje.estado !== "anulado";
        return (
          _filtroFecha &&
          _filtroVehiculo &&
          _filtroCliente &&
          _filtroServicio &&
          _filtroError &&
          _filtroEstado
        );
      });
      pesajes = ordenarPesajes(pesajes);
      return pesajes;
    });

    watch(pesaje, (newVal) => {
      if (newVal) {
        imagenes.nuevas = [];
        imagenes.eliminar = [];
      }
    });

    const abrirDialogoInfo = (item: Pesaje): void => {
      if (!item.id) return;
      const _pesaje = _pesajes.getById(item.id);
      if (pesaje.value?.id === item.id) {
        dialogoEditar.value = false;
        dialogoInfo.value = false;
        pesaje.value = null;
      } else if (_pesaje) {
        pesaje.value = _pesaje;
        dialogoInfo.value = true;
        dialogoEditar.value = false;
      }
    };

    const clickInformacion = (_pesaje: Pesaje): void => {
      if (dialogoEditar.value) {
        abrirDialogoConfirmarSinGuardar(_pesaje);
      } else {
        abrirDialogoInfo(_pesaje);
      }
    };

    const abrirDialogoEditar = (): void => {
      const id = pesaje.value?.id;
      if (!id) return;
      const _pesaje = _pesajes.getById(id);
      if (_pesaje) {
        pesaje.value = _pesaje;
        dialogoInfo.value = false;
        dialogoEditar.value = true;
      }
    };

    const cerrarDialogoEditar = (): void => {
      dialogoEditar.value = false;
      pesaje.value = null;
    };

    const abrirDialogoAnular = (_pesaje: Pesaje): void => {
      dialogoConfirmacion.model = true;
      dialogoConfirmacion.texto = t(
        "pesajes.mensaje-confirmacion.message.0",
      ) as string;
      dialogoConfirmacion.si = () => {
        const doc = Object.assign({}, _pesaje);
        doc.estado = "anulado";
        _pesajes.set(doc);
        cerrarDialogoConfirmacion();
      };
      dialogoConfirmacion.no = () => {
        cerrarDialogoConfirmacion();
      };
    };

    const abrirDialogoConfirmarSinGuardar = (_pesaje: Pesaje): void => {
      dialogoConfirmacion.model = true;
      dialogoConfirmacion.texto = t(
        "pesajes.mensaje-confirmacion.message.1",
      ) as string;
      dialogoConfirmacion.si = () => {
        abrirDialogoInfo(_pesaje);
        cerrarDialogoConfirmacion();
      };
      dialogoConfirmacion.no = () => {
        cerrarDialogoConfirmacion();
      };
    };

    const cerrarDialogoConfirmacion = (): void => {
      dialogoConfirmacion.model = false;
      dialogoConfirmacion.texto = "";
      dialogoConfirmacion.si = undefined;
      dialogoConfirmacion.no = undefined;
    };

    const abrirEditarImagenes = (): void => {
      mostrarGaleria.value = true;
      editarGaleria.value = true;
    };

    const abrirGaleria = (): void => {
      mostrarGaleria.value = true;
      editarGaleria.value = false;
    };

    const cerrarGaleria = (): void => {
      mostrarGaleria.value = false;
      editarGaleria.value = false;
    };

    const abrirDialogoExito = (): void => {
      dialogoExito.model = true;
      dialogoExito.mensaje = t("pesajes.dialogo-exito.message.0") as string;
      dialogoExito.icono = "ag-save";
    };

    const cerrarDialogoExito = (): void => {
      dialogoExito.model = false;
      dialogoExito.mensaje = "";
      dialogoExito.icono = "";
    };

    const eliminarFotoArray = async (fotos: Archivo[], foto: Archivo) => {
      if (foto.id) {
        await _archivos.drop(foto.id);
        const index = fotos.findIndex((_foto) => _foto.id === foto.id);
        if (index > -1) {
          fotos.splice(index, 1);
        }
      }
      return fotos;
    };

    const agregarFotoArray = async (fotos: Archivo[], foto: BlobArchivo) => {
      const id = await _archivos.setBlob(foto);
      const _foto = _archivos.getById(id);
      if (_foto) {
        fotos.push(_foto);
      }
      return fotos;
    };

    const guardarPesaje = async (
      _pesaje: Pesaje | PesajePatch,
    ): Promise<void> => {
      if (_pesaje) {
        cargando.value = true;
        let fotos = pesaje.value?.fotos || [];
        for (const archivo of imagenes.eliminar) {
          fotos = await eliminarFotoArray(fotos, archivo);
        }
        for (const archivo of imagenes.nuevas) {
          fotos = await agregarFotoArray(fotos, archivo);
        }
        _pesaje.fotos = fotos;
        _pesaje.sincronizacion = {
          enviado: new Date().toISOString(),
          recibido: null,
        };
        cerrarGaleria();
        _pesajes.set(_pesaje);
        cerrarDialogoEditar();
        abrirDialogoExito();
        cargando.value = false;
      }
    };

    const errorCliente = (_pesaje: Pesaje | null): boolean => {
      const id = _pesaje?.cliente?.id ?? "";
      const _cliente = _clientes.getById(id);
      return !_cliente;
    };

    const errorServicio = (_pesaje: Pesaje | null): boolean => {
      const id = _pesaje?.servicio?.id ?? "";
      const _servicio = _servicios.getById(id);
      return !_servicio;
    };

    const errorCapacidad = (_pesaje: Pesaje | null): boolean => {
      if (!_pesaje) return true;
      const noData = !_pesaje.capacidad;
      const noNumber = isNaN(_pesaje.capacidad);
      const noPositive = _pesaje.capacidad <= 0;
      return noData || noNumber || noPositive;
    };

    const errorCampos = (_pesaje: Pesaje | null): boolean => {
      if (!_pesaje?.vehiculo || !_pesaje?.desecho || !_pesaje?.ruta)
        return true;
      if (!_pesaje?.entrada?.fecha || !_pesaje?.entrada?.chofer) return true;
      if (
        !_pesaje.entrada.peso ||
        isNaN(_pesaje.entrada.peso) ||
        _pesaje.entrada.peso <= 0
      )
        return true;
      if (!_pesaje?.salida?.fecha || !_pesaje?.salida?.chofer) return true;
      return (
        !_pesaje.salida.peso ||
        isNaN(_pesaje.salida.peso) ||
        _pesaje.salida.peso <= 0
      );
    };

    const errorImg = (_pesaje: Pesaje): boolean => {
      const id = _pesaje.cliente?.id ?? "";
      const _cliente = _clientes.getById(id);
      if (_cliente && _cliente.tipo.toLowerCase() === "municipal") {
        return _pesaje.fotos.length === 0;
      }
      return false;
    };

    const onClickEditarPesaje = async (_pesaje: Pesaje): Promise<void> => {
      if (dialogoEditar.value) {
        cerrarDialogoEditar();
        await nextTick();
      }
      dialogoEditar.value = true;
      dialogoInfo.value = false;
      pesaje.value = _pesaje;
    };

    const resincronizar = (pesaje: Pesaje) => {
      if (!pesaje.id) return;
      _pesajes.resynchronize(pesaje.id);
    };

    return {
      usuario,
      usuarios,
      archivos,
      permisos,
      clientes,
      servicios,
      pesaje,
      filtros,
      imagenes,
      cargando,
      dialogoInfo,
      dialogoExito,
      dialogoEditar,
      editarGaleria,
      datosFiltrados,
      mostrarGaleria,
      clientesOrdenados,
      choferesOrdenados,
      desechosOrdenados,
      serviciosOrdenados,
      vehiculosOrdenados,
      dialogoConfirmacion,
      rutasPesajeOrdenadas,
      abrirGaleria,
      cerrarGaleria,
      guardarPesaje,
      resincronizar,
      clickInformacion,
      abrirDialogoAnular,
      abrirDialogoEditar,
      cerrarDialogoExito,
      abrirEditarImagenes,
      onClickEditarPesaje,
    };
  },
});
